import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import { selectCreditCards } from '../../store/selectors/creditCardSelectors';
import { fetchCreditCards } from '../../store/actions/creditCard/creditCardActions';
import { CREDIT_CARD_LOADING } from '../../store/actions/creditCard/creditCardActionConstants';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import Section from '../../components/Section/Section';
import BlockSectionLoader from '../../components/Loader/BlockSectionLoader';
import RemoveCreditCardModal from '../../components/Modals/CreditCard/RemoveCreditCardModal';
import Paragraph from '../../components/Paragraph/Paragraph';
import CreditCard from '../../components/CreditCard.js/CreditCard';
import { BankAccountCardEmpty } from '../../components/BankAccountsCard/BankAccountCardTable';
import { BankAccountsPageGrid } from './BankAccountsPage';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { useBrokerName } from '../../util/hooks/userBrokerName';
import { removeFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ADD_CREDIT_CARD_WIZARD,
  IS_CARD_USED_FOR_AUTO_PAY,
  IS_MODAL_OPEN,
} from '../../constants/sessionStorage';
import NagDashboardBanner from '../../components/NagDashboard/NagDashboardBanner';
import ChangeAutoPaySettingsModal from '../../components/Modals/ChangeAutoPaySettings/ChangeAutoPaySettingsModal';
import useGtmHook from '../../util/hooks/useGtmHook';
import { ACCOUNT_DATA_LOADED } from '../../constants/gtmEvents';
import AutoPayStatus from '../../components/StorageFee/AutoPayStatus';
import { useSessionStorageState } from '../../util/hooks/useSessionStorageState';
import { setWizardContent } from '../../util/helpers/wizardHelpers';
import ReferralBanner from '../../components/ReferralBanner/ReferralBanner';
import AddCreditDebitCardWizard from '../../components/Modals/CreditCard/AddCreditDebitCardWizard';
import themeColors from '../../assets/styles/themeColors';
import ComingSoonBanner from '../../components/ComingSoon/ComingSoon';
import LaunchBanner from '../../components/LaunchBanner/LaunchBanner';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';

const CreditCardsPageContainer = styled.div`
  margin: ${pxToRem(24)} 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: 0 ${pxToRemMd(16)};
    margin: ${pxToRemMd(24)} 0;
  }
`;

const CreditCardsContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddPaymentMethodButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
  margin-bottom: ${pxToRem(10)};
`;

const CreditCardsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const creditCards = useSelector(selectCreditCards);
  const accountUid = useSelector(getCurrentAccountUid);
  const brokerName = useBrokerName();

  const [removeCreditCardModalOpen, setRemoveCreditCardModalOpen] = useState(
    false,
  );
  const [removeCreditCardId, setRemoveCreditCardId] = useState(null);
  const [
    isAddCreditCardModalOpen,
    setIsAddCreditCardModalOpen,
  ] = useSessionStorageState(IS_MODAL_OPEN, false);
  const [isAutoPaySettingsModalOpen, setIsAutoPaySettingsModalOpen] = useState(
    false,
  );
  const isLoadingCreditCards = useSelector(
    selectIsLoadingByActionType(CREDIT_CARD_LOADING),
  );

  const [isOpenPayStorageFeeByCard] = useState(false);

  const settings = useSelector(selectSettings);
  const { WelcomeBannerRequired } = settings;

  const isAutoPayOn = creditCards?.some((card) => card.IsDefaultForStorage);

  const { gtmScreenView, appGtmEvent } = useGtmHook();
  const { CoomingSoonBannerRequired } = useSelector(selectSettings);
  const { brokerTheme } = config;
  const isHAA = brokerTheme === 'HardAssetsAlliance';

  useEffect(() => {
    gtmScreenView({
      path: window.location.pathname,
      title: 'Credit Cards',
    });
    appGtmEvent(ACCOUNT_DATA_LOADED);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchCreditCards({ accountUid }));
    }
  }, [accountUid, dispatch]);

  const handleRemoveCreditCard = (creditCardUid) => {
    setRemoveCreditCardModalOpen(true);
    setRemoveCreditCardId(creditCardUid);
  };

  const handleRemoveModalClose = () => {
    setRemoveCreditCardModalOpen(false);
    setRemoveCreditCardId(null);
    removeFromSessionStorage(IS_CARD_USED_FOR_AUTO_PAY);
  };

  const handleCloseAutoPaySettings = () => {
    setIsAutoPaySettingsModalOpen(false);
    removeFromSessionStorage(IS_CARD_USED_FOR_AUTO_PAY);
  };

  return (
    <>
      {isHAA && CoomingSoonBannerRequired && <ComingSoonBanner />}
      {isHAA && WelcomeBannerRequired && <LaunchBanner />}
      <ReferralBanner />
      <NagDashboardBanner />
      <NavigationBar
        mainPage={t('header.navDropdownAccount')}
        subPage={t('header.linkCreditCards')}
      />
      <Section>
        <CreditCardsPageContainer>
          <Paragraph marginBottom={32}>
            {t('creditCards.creditCardIntro', { brokerName })}
          </Paragraph>
          <BankAccountsPageGrid>
            <CreditCardsContent>
              {isLoadingCreditCards ? (
                <BlockSectionLoader isLoading={isLoadingCreditCards} />
              ) : (
                <>
                  <AddPaymentMethodButton
                    onClick={() => {
                      setIsAddCreditCardModalOpen(true);
                      setWizardContent(IS_MODAL_OPEN, true);
                    }}
                  >
                    {t('creditCards.addNewCreditCard')}
                  </AddPaymentMethodButton>

                  {!isEmpty(creditCards) ? (
                    <>
                      {creditCards?.map((creditCardItem) => (
                        <CreditCard
                          creditCard={creditCardItem}
                          key={creditCardItem.CreditCardUid}
                          handleRemoveCreditCard={handleRemoveCreditCard}
                        />
                      ))}
                    </>
                  ) : (
                    <BankAccountCardEmpty>
                      <p>{t('creditCards.emptyList')}</p>
                    </BankAccountCardEmpty>
                  )}
                </>
              )}
            </CreditCardsContent>
          </BankAccountsPageGrid>
          <>
            <Paragraph bold marginTop={24}>
              {t('storageFee.payStorageFeeByCardModal.storageFeePayments')}
            </Paragraph>

            <AutoPayStatus
              creditCard={
                creditCards?.find((card) => card.IsDefaultForStorage) ?? null
              }
              isAutoPayOn={isAutoPayOn}
              setIsAutoPaySettingsModalOpen={setIsAutoPaySettingsModalOpen}
              isOpenPayStorageFeeByCard={isOpenPayStorageFeeByCard}
              showChangeSettingsButton
              isOnBalancesPage
            />
          </>
        </CreditCardsPageContainer>
      </Section>
      <AddCreditDebitCardWizard
        isOpen={isAddCreditCardModalOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_CREDIT_CARD_WIZARD);
          setIsAddCreditCardModalOpen(false);
        }}
        // postPaidFlow
        // setIsNewCardAdded={setIsNewCardAdded}
        // setIsNewACHAdded={setIsNewACHAdded}
        // goToInitialModal={() => {}}
        // avoidClearingStorage
        // setIsAddingNewCard={setIsAddingNewCard}
      />
      {/* <AddCreditCardWizard
        isOpen={isAddCreditCardModalOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_CREDIT_CARD_WIZARD);
          setIsAddCreditCardModalOpen(false);
        }}
      /> */}
      <RemoveCreditCardModal
        isOpen={removeCreditCardModalOpen}
        onClose={handleRemoveModalClose}
        isDefaultForStorage={
          creditCards?.find(
            (card) => card?.CreditCardUid === removeCreditCardId,
          )?.IsDefaultForStorage
        }
        setIsAutoPaySettingsModalOpen={setIsAutoPaySettingsModalOpen}
        removeCreditCardModalOpen={removeCreditCardModalOpen}
        setRemoveCreditCardModalOpen={setRemoveCreditCardModalOpen}
        creditCard={creditCards.find(
          (card) => card.CreditCardUid === removeCreditCardId,
        )}
      />
      <ChangeAutoPaySettingsModal
        isOpen={isAutoPaySettingsModalOpen}
        handleClose={handleCloseAutoPaySettings}
        removeCreditCardModalOpen={removeCreditCardModalOpen}
        setRemoveCreditCardModalOpen={setRemoveCreditCardModalOpen}
      />
    </>
  );
};

export default CreditCardsPage;
